import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  signal,
  computed,
  input,
  output,
  effect,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subject, merge, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DatePickerDirective, SpinDirective } from '../../shared/directives';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { ModalStatusComponent } from '../modal-status/modal-status';
import { HuxleyComponent } from '../huxley/huxley';
import { SectionIconComponent, SectionIconKey } from '../section-icon/section-icon';
import { StepIndicatorComponent, StepConfig } from '../step-indicator/step-indicator';
import { ButtonComponent } from '../button/button';
import { ToolbarSearchComponent } from '../toolbar-search/toolbar-search';
import { EmptyStateComponent } from '../empty-state/empty-state';
import { PickerSkeletonComponent } from '../picker-skeleton/picker-skeleton';
import { CompositeReusePanelComponent } from '../composite-reuse-panel/composite-reuse-panel';
import { FrameworkIconComponent } from '../framework-icon/framework-icon';
import { KindIconComponent } from '../kind-icon/kind-icon';
import { EntityIconComponent, EntityIconInput } from '../entity-icon/entity-icon';
import { LanguagePickerComponent } from '../language-picker/language-picker';
import { LanguageBadgeComponent } from '../language-badge/language-badge';
import { ControlRefComponent } from '../control-ref/control-ref';
import { templateFrameworkIdentity, matchFrameworkIdentity } from '../../utils/framework.utils';
import { compositeCoverageForSelection } from '../../utils/import-preview.utils';

import {
  AssessmentRecord,
  AssessmentTypeEnum,
  ControlCategoryEntry,
  ControlCreateRequest,
  ControlRecord,
  ControlRecurrence,
  ImplementationStatus,
  ControlTaskSpec,
  ControlTemplate,
  CreateEntityCategory,
  DOCUMENT_CONTENT_TYPES,
  ExistingControlMatch,
  FrameworkRecord,
  FRAMEWORK_METADATA,
  FrameworkType,
  EntityLanguage,
  Organization,
  DocumentRecord,
  RecurrenceUnit,
  TaskType,
} from '../../models';
import { AuthService } from '../../services/auth/auth';
import type { AppLocale } from '../../services/locale/locale.service';
import { StateStoreService } from '../../services/state-store/state-store';
import { LocaleService } from '../../services/locale/locale.service';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { getDocumentSubtypeLabel, getDocKindFromSubtypeCode } from '../../utils/document.utils';
import { formatDate as sharedFormatDate } from '../../utils/date.utils';
import type { LabeledOption } from '../../models/labeled-option.model';
import { toControlCategorySelectOptions } from '../../utils/control-category.utils';
import { localizedField } from '../../utils/locale.utils';
import {
  apiFieldsToRecurrencePreset,
  recurrencePresetToApiFields,
  toControlRecurrenceSelectOptions,
} from '../../utils/control-recurrence.utils';
import { getControlStatusLabelKey } from '../../utils/control-status.utils';
import { accessDenialMessage } from '../../utils/access-error.utils';
import { getControlType } from '../../utils/control.utils';
import { getFrameworkLabel } from '../../utils/framework.utils';
import { getPlugin } from '../../services/assessments/assessment-type.registry';
import { injectActiveTranslation } from '../../services/locale/active-translation';
import { VENDOR_PRIVACY_ROLE_LABELS } from '../../models/vendor/common.model';
import { AssigneePickerComponent } from '../assignee-picker/assignee-picker';
import { getEntityLanguageLabel } from '../../utils/language.utils';

type ModalStep = 'type' | 'language' | 'pick' | 'details' | 'config' | 'tasks' | 'review';

/**
 * Row-state selection for control creation. Sits at the front of the
 * wizard as its own step, replacing the previous in-Details Single |
 * Composite toggle. Every downstream step (details, config, tasks,
 * review) contextualises on the choice:
 *   * ``'custom'``   → Custom row-state. No template affordance in Details.
 *   * ``'template'`` → Linked row-state (single template lineage).
 *                      Template picker in Details.
 *   * ``'composite'``→ Composite row-state (≥2 template lineage).
 *                      Multi-select template picker + composite reuse
 *                      panel in Details (VER-284 — see plan).
 *
 * The choice is a signal (not a form control) because it drives step
 * rendering, not form validation. Callers can preselect via the
 * ``initialType`` input to skip the type step (e.g. the DPIA measure
 * picker's card grid emits which type it wants, then opens the modal
 * with that seeded so the user sees Details immediately).
 */
type ControlCreationType = 'custom' | 'template' | 'composite';

/**
 * Link kinds for a task template defined inline in the Tasks step.
 * `this_control` is a placeholder used when the task auto-links to the control
 * being created (Upload tasks). The actual control_id is resolved on submit.
 */
type TaskLinkKind = 'document' | 'control' | 'framework' | 'vendor' | 'assessment' | 'this_control';

interface TaskTemplate {
  title: string;
  task_type: TaskType;
  link_kind: TaskLinkKind | null;
  link_entity_id: string | null;
  description: string;
  needs_approval: boolean;
  recurring: boolean;
  due_date: string | null;
  create_entity_subtype: string | null;
}

/** Display-ready row for the searchable entity list under the inline
 * task editor. `iconInput` is a baked view-model that <app-entity-icon>
 * consumes directly — the surface owns the source-record → icon-input
 * mapping so the primitive stays dumb. */
interface EntityItem {
  id: string;
  name: string;
  subtitle?: string;
  iconInput: EntityIconInput;
}

@Component({
  selector: 'app-create-control-modal',
  templateUrl: './create-control-modal.html',
  styleUrl: './create-control-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LowerCasePipe,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatSlideToggleModule,
    TranslocoModule,
    DatePickerDirective,
    SpinDirective,
    ModalComponent,
    ModalStatusComponent,
    HuxleyComponent,
    SectionIconComponent,
    StepIndicatorComponent,
    ButtonComponent,
    ToolbarSearchComponent,
    EmptyStateComponent,
    PickerSkeletonComponent,
    AssigneePickerComponent,
    CompositeReusePanelComponent,
    FrameworkIconComponent,
    KindIconComponent,
    EntityIconComponent,
    LanguagePickerComponent,
    LanguageBadgeComponent,
    ControlRefComponent,
    RouterLink,
  ],
})
export class CreateControlModal {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly stateStore = inject(StateStoreService);
  private readonly api = inject(OrgManagementApi);
  private readonly locale = inject(LocaleService).locale;
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly activeTranslation = injectActiveTranslation();

  // Inputs
  readonly isVisible = input(false);
  /** Optional prefills — surface driven by an upstream flow that already
   *  knows the shape (currently the DPIA measure → "Create custom control"
   *  chain). Any subset may be provided; unset prefills leave the form
   *  at its own defaults. Applied ONCE per visibility flip and reset on
   *  close so a subsequent reopen with different prefills re-patches. */
  readonly pendingName = input<string | null>(null);
  readonly pendingDescription = input<string | null>(null);
  readonly pendingImplementationStatus = input<ImplementationStatus | null>(null);
  /** Caller-provided seed for the type step. When set, the modal opens
   *  on Details with the type preselected — matches the DPIA measure
   *  picker's card-grid entry pattern where the user has already made
   *  the type choice before the modal appears. When null the modal
   *  shows the type step first. */
  readonly initialType = input<ControlCreationType | null>(null);

  // Outputs
  readonly controlCreated = output<ControlRecord>();
  readonly cancelled = output<void>();

  /** Guards the one-shot prefill patch — flips true on first apply,
   *  resets on modal close. Prevents subsequent parent re-renders from
   *  clobbering user edits within the same open session. */
  private readonly _hasPatchedFromPrefill = signal(false);
  /** Implementation-status override captured at prefill time — the form
   *  doesn't surface this field visually, so we hold it separately and
   *  include it in the submitted `ControlCreateRequest` when set. */
  private readonly _prefillImplementationStatus = signal<ImplementationStatus | null>(null);

  // Step state — uniform across user types; the org context comes from
  // global state (`managedCustomerOrgId` for managers, `selectedOrgId`
  // for standard users) and is communicated via the in-modal context
  // badge, not via a wizard step.
  /** Ordered step list — depends on the row-state chosen on the Type
   *  step. Custom controls skip the ``'pick'`` step entirely; Template
   *  / Composite include it as the dedicated template-picker + reuse-
   *  panel screen. Derived so the step indicator + navigation stay in
   *  lockstep with the type choice — no hidden branches, no ghost
   *  step-indicator pills, no divergent onContinue/onBack code. */
  readonly steps = computed<ModalStep[]>(() => {
    const t = this._selectedType();
    const includePick = t === 'template' || t === 'composite';
    // Language sits immediately after Type — it locks the content
    // language for the ENTIRE downstream flow (template picks are
    // shown in this language, custom-path text is authored in it,
    // AI-generated fields honour it). Was previously inline on the
    // `pick` step's template-section header, which silently skipped
    // the Custom flow — hidden gap fixed here.
    return includePick
      ? ['type', 'language', 'pick', 'details', 'config', 'tasks', 'review']
      : ['type', 'language', 'details', 'config', 'tasks', 'review'];
  });
  readonly stepConfigs = computed<StepConfig[]>(() => {
    this.activeTranslation();
    return this.steps().map(key => {
      // The `language` step reuses the shared `language_step.step_label`
      // key across every wizard so the pill label is uniform.
      const label =
        key === 'language'
          ? this.transloco.translate('language_step.step_label')
          : this.transloco.translate(`modals.create_control.step_${key}`);
      let detail: string | undefined;
      switch (key) {
        case 'language':
          detail = getEntityLanguageLabel(this.templateLanguage());
          break;
        case 'type': {
          const type = this.selectedType();
          if (type) detail = this.transloco.translate(`modals.create_control.card_${type}_title`);
          break;
        }
        case 'pick': {
          const template = this.selectedControlTemplate();
          const count = this.selectedControlTemplates().length;
          if (this.selectedType() === 'composite' && count) {
            detail = this.transloco.translate('step_indicator.selected_count', { count });
          } else if (template) {
            detail = this.localizedField(template.name, template.name_da);
          }
          break;
        }
      }
      return { key, label, detail };
    });
  });
  private readonly _currentStep = signal<ModalStep>('type');
  readonly currentStep = this._currentStep.asReadonly();

  /**
   * High-water mark for the step timeline. Advances only on forward
   * navigation; stays put on Back or timeline-click. Reset by
   * `resetModalState` and the modal-open effect so a fresh launch
   * starts clean.
   *
   * `selectType` regresses this back to 'type' whenever the type
   * changes — a different type reshapes the flow (custom has no
   * `pick` step) and invalidates the row-state signals selectType
   * already clears, so any prior "visited" state past `type` would
   * point at data that no longer applies.
   */
  private readonly _maxReachedStep = signal<ModalStep>('type');
  readonly maxReachedStep = this._maxReachedStep.asReadonly();

  /** Forward-move helper — pairs step-set with high-water bookkeeping. */
  private setStep(next: ModalStep): void {
    this._currentStep.set(next);
    const list = this.steps();
    const nextIdx = list.indexOf(next);
    const maxIdx = list.indexOf(this._maxReachedStep());
    if (nextIdx > maxIdx) {
      this._maxReachedStep.set(next);
    }
  }

  /**
   * Timeline click — jump to a previously-visited step. All row-state
   * signals (template picks, composite reuse ids, task drafts, form
   * values, control-name, etc.) persist across jumps, so revisiting
   * is lossless. The step-indicator already gates to visited-and-not-
   * current; verify again here for defence in depth.
   */
  protected onStepClicked(key: string): void {
    if (this.isTerminalState()) return;
    const list = this.steps();
    const clickedIdx = list.indexOf(key as ModalStep);
    const maxIdx = list.indexOf(this._maxReachedStep());
    if (clickedIdx < 0 || clickedIdx > maxIdx) return;
    this.clearError();
    this._currentStep.set(key as ModalStep);
  }

  /** Type-step signal. Drives step rendering and template-picker mode.
   *  Set by the type-card click OR the ``initialType`` one-shot latch.
   *  ``null`` = user hasn't chosen yet (type step disables Next). */
  private readonly _selectedType = signal<ControlCreationType | null>(null);
  readonly selectedType = this._selectedType.asReadonly();

  // ── Composite reuse (VER-284) ─────────────────────────────────────
  // Two-way models bound to <app-composite-reuse-panel>. Populated by
  // the user picking checkboxes on the panel; consumed by ``submitControl``
  // when the composite type is active. Empty when no reuse candidates
  // exist / user picks "Create new only".
  readonly compositeReuseIds = signal<readonly string[]>([]);
  readonly compositeCreateGapFiller = signal<boolean>(true);
  /** Candidate matches for the current template/composite selection,
   *  resolved by ``GET /controls/existing-for-templates``. Empty until
   *  the debounced effect below fires; empty forever if the org has no
   *  live controls covering the selection. Shared across both flows:
   *  in Composite it drives the reuse panel; in Template it drives the
   *  advisory hint below the picker. */
  readonly reuseCandidates = signal<readonly ExistingControlMatch[]>([]);
  readonly reuseCandidatesLoading = signal<boolean>(false);
  /** Non-null when the last candidate fetch failed. Consumed by the
   *  reuse panel + Template hint to render a "couldn't check" state
   *  instead of a false "no duplicates" empty. Cleared on next fetch
   *  attempt (which happens automatically on any selection change). */
  readonly reuseCandidatesError = signal<string | null>(null);
  /** Per-open dismiss for the Template-mode advisory hint. Auto-clears
   *  when the picked template changes so a new pick re-surfaces the
   *  hint (dismissing is a per-decision choice, not a session mute). */
  readonly templateReuseHintDismissed = signal<boolean>(false);
  /** Manual retry trigger for the reuse-candidate fetch. Wired via the
   *  Subject → merged pipe below so a retry click re-runs the fetch
   *  with the current selection (bypasses ``distinctUntilChanged``). */
  private readonly _retryReuseFetch$ = new Subject<void>();
  /** Component-refs shape the reuse panel consumes — derived from the
   *  currently-selected composite templates. Empty string refs (a
   *  template with no ``control_reference``) are filtered out so the
   *  panel's coverage summary never counts an unnamed component. */
  readonly compositeReusePanelRefs = computed<readonly string[]>(() =>
    this.selectedControlTemplates()
      .map(t => t.control_reference)
      .filter((r): r is string => !!r && r.trim().length > 0)
  );

  /** Coverage view for the composite selection — same derivation the
   *  panel runs internally, mirrored here so ``pickValid`` can gate
   *  Next on "every component covered by reuse or gap-filler". */
  private readonly compositeCoverageView = computed(() =>
    compositeCoverageForSelection(
      this.compositeReusePanelRefs(),
      this.reuseCandidates(),
      this.compositeReuseIds()
    )
  );

  // Internal state
  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);
  /** True when {@link error} is a permission answer rather than a failure —
   *  swaps the red banner for the amber-lock one. A refused write is the
   *  system working, and red says something broke. */
  private readonly _errorIsDenial = signal(false);
  private readonly _successMessage = signal<string | null>(null);
  private _successTimer: ReturnType<typeof setTimeout> | null = null;
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly errorIsDenial = this._errorIsDenial.asReadonly();
  readonly successMessage = this._successMessage.asReadonly();

  /** Clear both halves together. The pair is only ever written through
   *  these three helpers, so the flag cannot outlive the message it
   *  describes and leave a stale amber banner on the next real failure. */
  private clearError(): void {
    this._error.set(null);
    this._errorIsDenial.set(false);
  }

  /**
   * Set the banner from a failed request.
   *
   * The chokepoint every call here goes through: a 403 becomes the server's
   * localised denial and flips the banner amber; anything else keeps the
   * server's prose, then `fallback`.
   */
  private setErrorFrom(err: unknown, fallback: string): void {
    const denial = accessDenialMessage(err, this.transloco);
    this._errorIsDenial.set(denial !== null);
    this._error.set(
      denial ??
        (err as { error?: { message?: string } })?.error?.message ??
        (err as { message?: string })?.message ??
        fallback
    );
  }

  /** Set a banner we composed ourselves — never a denial. */
  private setError(message: string): void {
    this._errorIsDenial.set(false);
    this._error.set(message);
  }

  readonly isTerminalState = computed(() => this._isLoading() || !!this._successMessage());

  // Org context — managers operate on the TopNav-picked customer; non-
  // managers operate on their own selected org. Single resolution, used
  // throughout the wizard.
  readonly isManager = this.stateStore.isManager;
  readonly selectedOrg = computed<Organization | null>(() => {
    const id = this.isManager()
      ? this.stateStore.managedCustomerOrgId()
      : (this.stateStore.selectedOrgId() ?? null);
    if (!id) return null;
    return this.stateStore.organizations().find(o => o.principal_id === id) ?? null;
  });
  readonly orgDocuments = signal<DocumentRecord[]>([]);
  readonly loadingDocuments = signal(false);

  // Assignees
  readonly selectedAssignees = signal<string[]>([]);
  protected readonly orgMembers = this.stateStore.orgMembers;

  readonly currentUserName = computed(() => {
    this.activeTranslation();
    const user = this.authService.user();
    if (user?.given_name && user?.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    return user?.email ?? this.transloco.translate('modals.create_control.current_user_fallback');
  });

  readonly documentOptions = computed(() =>
    this.orgDocuments().map(doc => ({
      id: doc.document_id,
      name: doc.name,
      typeLabel: getDocumentSubtypeLabel(doc, this.locale()),
      status: doc.status,
      // Kind drives <app-kind-icon> — same silhouette + tint the
      // documents page and vendor evidence use, so the picker option
      // reads identically to how the doc appears elsewhere.
      kind: getDocKindFromSubtypeCode(doc.type),
    }))
  );

  readonly recurrenceOptions = computed(() => {
    this.activeTranslation();
    return toControlRecurrenceSelectOptions(key => this.transloco.translate(key));
  });

  /**
   * Categories are stored as their canonical English string in the backend so
   * the value sent across the wire is stable. The `label` is what the user
   * sees in the dropdown and the review panel.
   */
  readonly categoryOptions = computed<readonly LabeledOption[]>(() => {
    this.activeTranslation();
    const locale = this.templateLanguage() as AppLocale;
    return toControlCategorySelectOptions(
      this.controlCategories(),
      key => this.transloco.translate(key),
      locale
    );
  });

  readonly orgFrameworks = signal<FrameworkRecord[]>([]);
  readonly loadingFrameworks = signal(false);
  readonly controlCategories = signal<readonly ControlCategoryEntry[]>([]);

  // ─────────────────────────────────────────────────────────────────────────
  // Template linking — optional pre-fill from a platform control template.
  // ─────────────────────────────────────────────────────────────────────────

  private readonly _controlTemplates = signal<ControlTemplate[]>([]);
  private readonly _controlTemplatesLoading = signal(false);
  readonly controlTemplatesLoading = this._controlTemplatesLoading.asReadonly();
  readonly selectedControlTemplate = signal<ControlTemplate | null>(null);
  /** Composite (multi-template) selection — populated only when
   *  ``selectedType()`` is ``'composite'``. Kept as a distinct signal
   *  from ``selectedControlTemplate`` so navigating back to the Type
   *  step and changing mind doesn't lose the other mode's selection;
   *  either can be non-empty but ``buildRequest`` enforces mutual
   *  exclusion on save via the type gate. */
  readonly selectedControlTemplates = signal<ControlTemplate[]>([]);
  /** DERIVED from ``selectedType()`` — replaces the previous in-Details
   *  Single|Composite toggle. Kept as a computed so template rendering,
   *  save-shape branching, and existing ``.templatePickerMode()``
   *  read-sites don't have to know a refactor happened. Composite mode
   *  when the type step chose composite; single-template mode
   *  otherwise (custom + template both use the single-select picker
   *  for the "browse a template" affordance, though custom starts with
   *  no template selected and doesn't require one to advance). */
  readonly templatePickerMode = computed<'single' | 'composite'>(() =>
    this._selectedType() === 'composite' ? 'composite' : 'single'
  );
  readonly templateLanguage = signal<EntityLanguage>(this.stateStore.orgDefaultContentLanguage());
  readonly templateSearchQuery = signal('');

  readonly filteredControlTemplates = computed(() => {
    const q = this.templateSearchQuery().toLowerCase().trim();
    const all = this._controlTemplates();
    if (!q) return all;
    return all.filter(
      t =>
        t.control_reference?.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        t.name_da?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q)
    );
  });

  /** Set of ``template_id``s currently selected in composite mode —
   *  fast membership lookup for the multi-select checkbox binding. */
  readonly selectedCompositeTemplateIds = computed(
    () => new Set(this.selectedControlTemplates().map(t => t.id))
  );

  /** Returns the localised value, falling back to English when Danish is absent. */
  localizedField(en: string | null | undefined, da: string | null | undefined): string {
    return localizedField(en, da, this.templateLanguage());
  }

  /** Branched singular/plural key for the composite-selection chip
   *  label. The repo has no @jsverse/transloco-messageformat plugin,
   *  so ICU plural syntax renders raw — swap in explicit keys. */
  protected compositeSelectedKey(): string {
    return this.selectedControlTemplates().length === 1
      ? 'modals.create_control.composite_selected_one'
      : 'modals.create_control.composite_selected_other';
  }

  /** Branched key for the Template-mode reuse advisory title. */
  protected templateReuseTitleKey(): string {
    return this.reuseCandidates().length === 1
      ? 'modals.create_control.template_reuse_title_one'
      : 'modals.create_control.template_reuse_title_other';
  }

  /** Branched key for the per-candidate "linked to N framework(s)"
   *  subline in the advisory. */
  protected templateReuseFrameworksKey(count: number): string {
    return count === 1
      ? 'modals.create_control.template_reuse_frameworks_one'
      : 'modals.create_control.template_reuse_frameworks_other';
  }

  readonly reviewTemplateRef = computed(() => this.selectedControlTemplate()?.control_reference);

  // ─────────────────────────────────────────────────────────────────────────
  // Tasks step — inline task-template editor (no backend submission yet;
  // templates are kept locally and bundled with the control on final submit).
  // ─────────────────────────────────────────────────────────────────────────

  readonly taskTypeOptions = computed<{ value: TaskType; label: string }[]>(() => {
    this.activeTranslation();
    const t = (k: string) => this.transloco.translate(`modals.create_control.${k}`);
    return [
      { value: TaskType.UPLOAD, label: t('task_type_upload') },
      { value: TaskType.REVIEW, label: t('task_type_review') },
      { value: TaskType.CREATE, label: t('task_type_create') },
      { value: TaskType.ASSESS, label: t('task_type_assess') },
      { value: TaskType.OTHER, label: t('task_type_other') },
    ];
  });

  /** Section-icon keys map each entity to the same SVG the sidebar renders,
   *  so the inline "Link to" mini-cards read identically to the standalone
   *  `<app-create-task-modal>`, `<app-panel-task-list>`, and the sidebar
   *  nav. Single vocabulary across surfaces. */
  readonly taskLinkOptions = computed<
    { value: TaskLinkKind; label: string; sectionIcon: SectionIconKey }[]
  >(() => {
    this.activeTranslation();
    const t = (k: string) => this.transloco.translate(`modals.create_control.${k}`);
    return [
      { value: 'document', label: t('link_kind_document'), sectionIcon: 'documents' },
      { value: 'control', label: t('link_kind_control'), sectionIcon: 'controls' },
      { value: 'framework', label: t('link_kind_framework'), sectionIcon: 'frameworks' },
      { value: 'vendor', label: t('link_kind_vendor'), sectionIcon: 'vendors' },
      { value: 'assessment', label: t('link_kind_assessment'), sectionIcon: 'assessment' },
    ];
  });

  private readonly _taskTemplates = signal<TaskTemplate[]>([]);
  readonly taskTemplates = this._taskTemplates.asReadonly();

  private readonly _taskFormOpen = signal(false);
  readonly taskFormOpen = this._taskFormOpen.asReadonly();

  private readonly _editingTaskIndex = signal<number | null>(null);
  readonly editingTaskIndex = this._editingTaskIndex.asReadonly();

  /**
   * Inline editor form for a single task template. Reset every time the
   * Add/Edit form is opened; never submitted on its own.
   */
  readonly taskForm = this.fb.group({
    title: ['', Validators.required],
    task_type: [null as TaskType | null, Validators.required],
    link_kind: [null as TaskLinkKind | null],
    link_entity_id: [null as string | null],
    create_entity_subtype: [null as string | null],
    description: [''],
    needs_approval: [false],
    recurring: [false],
    due_date: [null as string | null],
  });

  private readonly taskTitleValue = toSignal(this.taskForm.controls.title.valueChanges, {
    initialValue: '',
  });
  private readonly taskTypeValue = toSignal(this.taskForm.controls.task_type.valueChanges, {
    initialValue: null as TaskType | null,
  });
  private readonly taskLinkKindValue = toSignal(this.taskForm.controls.link_kind.valueChanges, {
    initialValue: null as TaskLinkKind | null,
  });
  private readonly taskLinkEntityIdValue = toSignal(
    this.taskForm.controls.link_entity_id.valueChanges,
    { initialValue: null as string | null }
  );
  private readonly createEntitySubtypeValue = toSignal(
    this.taskForm.controls.create_entity_subtype.valueChanges,
    { initialValue: null as string | null }
  );
  readonly taskRecurringValue = toSignal(this.taskForm.controls.recurring.valueChanges, {
    initialValue: false,
  });

  /** Helper text shown beneath the Task Type pills. */
  readonly taskTypeDescription = computed<string | null>(() => {
    this.activeTranslation();
    const t = (k: string) => this.transloco.translate(`modals.create_control.${k}`);
    switch (this.taskTypeValue()) {
      case TaskType.UPLOAD:
        return t('task_type_desc_upload');
      case TaskType.REVIEW:
        return t('task_type_desc_review');
      case TaskType.CREATE:
        return t('task_type_desc_create');
      case TaskType.ASSESS:
        return t('task_type_desc_assess');
      case TaskType.OTHER:
        return t('task_type_desc_other');
      default:
        return null;
    }
  });

  /** Whether the current task type requires the user to pick a linked entity. */
  readonly showEntityPicker = computed(() => {
    const t = this.taskTypeValue();
    return (
      t === TaskType.REVIEW ||
      t === TaskType.CREATE ||
      t === TaskType.ASSESS ||
      t === TaskType.OTHER
    );
  });

  /** Whether the current task type auto-links to the control being created. */
  readonly isUploadTask = computed(() => this.taskTypeValue() === TaskType.UPLOAD);

  readonly isCreateTask = computed(() => this.taskTypeValue() === TaskType.CREATE);
  readonly requiresLink = computed(() => {
    const t = this.taskTypeValue();
    return t === TaskType.REVIEW || t === TaskType.CREATE;
  });

  readonly documentSubtypeOptions = computed(() => {
    this.activeTranslation();
    return DOCUMENT_CONTENT_TYPES.map(dt => ({
      value: dt.value as string,
      label: dt.label,
      description: this.transloco.translate<string>(dt.descriptionKey),
    }));
  });

  readonly frameworkSubtypeOptions = (Object.keys(FRAMEWORK_METADATA) as FrameworkType[]).map(
    k => ({
      value: k as string,
      label: FRAMEWORK_METADATA[k].defaultName,
      description: FRAMEWORK_METADATA[k].defaultDescription,
    })
  );

  readonly createEntitySubtypeOptions = computed<
    { value: string; label: string; description: string }[]
  >(() => {
    const kind = this.taskLinkKindValue();
    if (kind === 'document') return this.documentSubtypeOptions();
    if (kind === 'framework') return this.frameworkSubtypeOptions;
    return [];
  });

  readonly showSubtypePicker = computed(() => {
    if (!this.isCreateTask()) return false;
    const kind = this.taskLinkKindValue();
    return kind === 'document' || kind === 'framework';
  });

  // Lazily-loaded data sources for the entity picker.
  private readonly _controls = signal<ControlRecord[]>([]);
  private readonly _controlsLoading = signal(false);
  private readonly _assessments = signal<AssessmentRecord[]>([]);
  private readonly _assessmentsLoading = signal(false);

  /** Free-text filter applied to the entity list under the inline task editor. */
  readonly entitySearchQuery = signal('');

  /** Display rows for the searchable entity list, keyed off `link_kind`. */
  readonly entityItems = computed<EntityItem[]>(() => {
    this.activeTranslation();
    const kind = this.taskLinkKindValue();
    switch (kind) {
      case 'document':
        return this.orgDocuments().map(d => ({
          id: d.document_id,
          name: d.name,
          subtitle: getDocumentSubtypeLabel(d, this.locale()),
          iconInput: { kind: 'document', docKind: getDocKindFromSubtypeCode(d.type) },
        }));
      case 'control':
        return this._controls().map(c => ({
          id: c.control_id,
          name: c.name,
          subtitle: this.transloco.translate(getControlStatusLabelKey(c.implementation_status)),
          iconInput: { kind: 'control', controlType: getControlType(c) },
        }));
      case 'framework':
        return this.orgFrameworks().map(f => ({
          id: f.framework_id,
          name: f.name,
          subtitle: getFrameworkLabel(f),
          iconInput: { kind: 'framework', frameworkType: f.framework_type },
        }));
      case 'vendor':
        return this.stateStore.vendors().map(v => ({
          id: v.vendor_id,
          // Best-effort name; the registry holds the canonical display name.
          name: v.vendor_id.replace(/^VENDOR#/, ''),
          subtitle: VENDOR_PRIVACY_ROLE_LABELS[v.privacy_role],
          iconInput: { kind: 'vendor' },
        }));
      case 'assessment':
        return this._assessments().map(a => ({
          id: a.assessment_id,
          name: a.assessment_name,
          subtitle: this.transloco.translate(getPlugin(a.assessment_type).labelKey),
          iconInput: { kind: 'assessment', assessmentType: a.assessment_type },
        }));
      default:
        return [];
    }
  });

  /** Template-side binding — the CREATE-task subtype picker builds an
   *  `EntityIconInput` inline from a subtype code, so the resolver
   *  needs to be reachable as a plain method. */
  protected readonly getDocKindFromSubtypeCode = getDocKindFromSubtypeCode;
  /** Framework-identity resolvers reachable from the template so
   *  `<app-control-ref>` inputs can be built inline per row without
   *  a duplicated view-model layer for every consumer. */
  protected readonly templateFrameworkIdentity = templateFrameworkIdentity;
  protected readonly matchFrameworkIdentity = matchFrameworkIdentity;

  readonly filteredEntityItems = computed<EntityItem[]>(() => {
    const q = this.entitySearchQuery().trim().toLowerCase();
    if (!q) return this.entityItems();
    return this.entityItems().filter(
      item => item.name.toLowerCase().includes(q) || (item.subtitle ?? '').toLowerCase().includes(q)
    );
  });

  /** True while the data source for the currently-picked link kind is loading. */
  readonly isEntityLoading = computed(() => {
    const kind = this.taskLinkKindValue();
    if (kind === 'document') return this.loadingDocuments();
    if (kind === 'framework') return this.loadingFrameworks();
    if (kind === 'control') return this._controlsLoading();
    if (kind === 'vendor') return this.stateStore.vendorsLoading();
    if (kind === 'assessment') return this._assessmentsLoading();
    return false;
  });

  /** Save button on the inline task form is enabled when the template is complete. */
  readonly taskFormSaveEnabled = computed(() => {
    const title = this.taskTitleValue();
    if (!title || !title.trim()) return false;
    const type = this.taskTypeValue();
    if (!type) return false;
    if (type === TaskType.UPLOAD) return true;
    if (this.showEntityPicker()) {
      const linkKind = this.taskLinkKindValue();
      if (!linkKind) return false;
      if (type === TaskType.CREATE) {
        if (linkKind === 'control' || linkKind === 'vendor') return true;
        return !!this.createEntitySubtypeValue();
      }
      if (!this.taskLinkEntityIdValue()) return false;
    }
    return true;
  });

  readonly frameworkOptions = computed(() =>
    this.orgFrameworks().map(fw => ({
      id: fw.framework_id,
      name: fw.name,
      // Framework type drives the certification-seal <app-framework-
      // icon> — same seal the framework detail page and import review
      // render so managers recognise the standard at a glance.
      framework_type: fw.framework_type,
    }))
  );

  /** Today's date in YYYY-MM-DD format (recomputed each time the modal opens). */
  readonly todayDate = signal(this.getTodayDate());

  readonly form = this.fb.group({
    name: ['', Validators.required],
    category: [null as string | null, Validators.required],
    description: [''],
    framework_ids: [[] as string[]],
    document_ids: [[] as string[]],
    recurrence: [ControlRecurrence.MONTHLY as ControlRecurrence | null],
    custom_recurrence_days: [null as number | null, [Validators.min(1)]],
    first_due_date: [this.todayDate()],
  });

  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: 'INVALID' });
  private readonly formValue = toSignal(this.form.valueChanges);
  /**
   * Configuration step is valid when the form passes its base validators AND, if
   * the user picked Custom recurrence, they've entered a positive interval.
   */
  private readonly formValid = computed(() => {
    if (this.formStatus() !== 'VALID') return false;
    if (this.recurrenceValue() === ControlRecurrence.CUSTOM) {
      const days = this.customRecurrenceDaysValue();
      return !!days && days >= 1;
    }
    return true;
  });

  private readonly recurrenceValue = toSignal(this.form.controls.recurrence.valueChanges, {
    initialValue: null,
  });
  private readonly customRecurrenceDaysValue = toSignal(
    this.form.controls.custom_recurrence_days.valueChanges,
    { initialValue: null as number | null }
  );
  readonly showDueDateBlock = computed(() => !!this.recurrenceValue());
  readonly isCustomRecurrence = computed(() => this.recurrenceValue() === ControlRecurrence.CUSTOM);
  readonly isControlRecurrent = computed(() => !!this.recurrenceValue());
  readonly showTaskDueDateField = computed(() => !this.taskRecurringValue());
  readonly customRecurrenceHint = computed(() => {
    this.activeTranslation();
    const days = this.customRecurrenceDaysValue();
    if (!days || days <= 0) return null;
    const key =
      days === 1
        ? 'modals.create_control.custom_recurrence_hint_one'
        : 'modals.create_control.custom_recurrence_hint_other';
    return this.transloco.translate(key, { days });
  });

  private readonly documentIdsValue = toSignal(this.form.controls.document_ids.valueChanges, {
    initialValue: [] as string[],
  });

  readonly selectedDocumentNames = computed(() => {
    const selectedIds: string[] = this.documentIdsValue() ?? [];
    return this.documentOptions()
      .filter(d => selectedIds.includes(d.id))
      .map(d => d.name)
      .join(', ');
  });

  readonly selectedFrameworkNames = computed(() => {
    const raw = this.formValue();
    const ids: string[] = raw?.framework_ids ?? [];
    return this.frameworkOptions()
      .filter(f => ids.includes(f.id))
      .map(f => f.name);
  });

  readonly selectedDocumentsList = computed(() => {
    const ids: string[] = this.documentIdsValue() ?? [];
    return this.documentOptions().filter(d => ids.includes(d.id));
  });

  readonly reviewRecurrenceLabel = computed(() => {
    const val = this.recurrenceValue();
    if (!val) return null;
    return this.recurrenceOptions().find(r => r.value === val)?.label ?? null;
  });

  /**
   * Resolves the selected recurrence to the `{ unit, interval }` pair the
   * backend expects. Preset options use MONTH + N months; CUSTOM uses DAY +
   * the user-entered day count. Returns null when no recurrence is picked
   * (or Custom has no valid value yet).
   */
  readonly effectiveRecurrence = computed<{ unit: RecurrenceUnit; interval: number } | null>(() => {
    const recurrence = this.recurrenceValue();
    if (!recurrence) return null;
    const fields =
      recurrence === ControlRecurrence.CUSTOM
        ? recurrencePresetToApiFields(recurrence, this.customRecurrenceDaysValue())
        : recurrencePresetToApiFields(recurrence);
    if (!fields) return null;
    return { unit: fields.recurrence_unit, interval: fields.recurrence_interval };
  });

  private readonly firstDueDateValue = toSignal(this.form.controls.first_due_date.valueChanges, {
    initialValue: this.form.controls.first_due_date.value,
  });

  /**
   * Number of task instances the backend will backfill immediately on creation.
   * Applies when the start date is in the past and at least one recurring task
   * is defined. The backend generates one instance per elapsed cycle per
   * recurring task, for every unit (MONTH or DAY) at the configured interval.
   */
  readonly backfillTaskCount = computed<number>(() => {
    const startStr = this.firstDueDateValue();
    const rec = this.effectiveRecurrence();
    const today = this.todayDate();

    if (!startStr || !rec || startStr >= today) return 0;

    const recurringCount = this.taskTemplates().filter(t => t.recurring).length;
    if (recurringCount === 0) return 0;

    const current = new Date(`${startStr}T00:00:00`);
    const todayD = new Date(`${today}T00:00:00`);
    let cycles = 0;

    while (current <= todayD && cycles < 1000) {
      cycles++;
      if (rec.unit === RecurrenceUnit.MONTH) {
        current.setMonth(current.getMonth() + rec.interval);
      } else if (rec.unit === RecurrenceUnit.DAY) {
        current.setDate(current.getDate() + rec.interval);
      } else {
        break;
      }
    }

    return cycles * recurringCount;
  });

  readonly recurringTaskDueDate = computed<string | null>(() => {
    const startStr = this.firstDueDateValue();
    const rec = this.effectiveRecurrence();
    if (!startStr || !rec) return null;
    const d = new Date(startStr);
    if (rec.unit === RecurrenceUnit.MONTH) {
      d.setMonth(d.getMonth() + rec.interval);
    } else {
      d.setDate(d.getDate() + rec.interval);
    }
    return sharedFormatDate(d.toISOString().split('T')[0], 'compact') || '—';
  });

  formatTaskDate(iso: string | null | undefined): string {
    return sharedFormatDate(iso, 'compact') || '—';
  }

  /** Details step requires name and category to be filled in before proceeding */
  private readonly nameValue = toSignal(this.form.controls.name.valueChanges, {
    initialValue: '',
  });
  private readonly categoryValue = toSignal(this.form.controls.category.valueChanges, {
    initialValue: null as string | null,
  });
  readonly detailsValid = computed(() => {
    const name = this.nameValue();
    const category = this.categoryValue();
    return !!name && name.trim().length > 0 && !!category;
  });

  /** Gate for the Pick step's Next button. Template requires exactly
   *  one template selected (single-template lineage is the whole point
   *  of that row-state); Composite requires ≥2 (the three-state model's
   *  ``Composite`` state is defined as ≥2 components) AND every picked
   *  component must be covered — either by a reuse pick or by the
   *  gap-filler that mints new controls for the uncovered refs.
   *  Otherwise the user would advance with a composite whose components
   *  neither reuse an existing control nor get created. Custom skips
   *  the Pick step entirely so this computed is only read when the
   *  step is actually rendered. */
  readonly pickValid = computed(() => {
    const type = this._selectedType();
    if (type === 'template') return !!this.selectedControlTemplate();
    if (type === 'composite') {
      if (this.selectedControlTemplates().length < 2) return false;
      const cov = this.compositeCoverageView();
      return this.compositeCreateGapFiller() || cov.uncoveredCount === 0;
    }
    return true;
  });

  /** Human-readable name of the selected category (uses the translated label). */
  readonly reviewCategoryLabel = computed<string | null>(() => {
    const val = this.categoryValue();
    if (!val) return null;
    return this.categoryOptions().find(c => c.value === val)?.label ?? val;
  });

  readonly modalConfig = computed<ModalConfig>(() => {
    this.activeTranslation();
    const step = this._currentStep();
    const terminal = this.isTerminalState();
    const t = (k: string) => this.transloco.translate(`modals.create_control.${k}`);
    // Shared shell across every wizard step \u2014 title, size, and the
    // chrome flags (logo, managed-customer caption, close, footer) are
    // identical per step. Per-step overrides only set the primary /
    // secondary buttons and their disabled state.
    const base = {
      title: t('modal_title'),
      size: 'large' as const,
      showCloseButton: !terminal,
      showFooter: !terminal,
      showLogo: true,
      showManagedCustomerContext: !terminal,
    };

    if (step === 'type') {
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_cancel'),
        // Next disabled until the user picks a row-state. The Pick
        // step (when reached) enforces the type-specific selection
        // rules (single-template lineage / ≥2 for composite).
        primaryButtonDisabled: this._selectedType() == null,
      };
    }

    if (step === 'language') {
      // `templateLanguage` is signal-initialised to the org default
      // and never nullable, so Next is always enabled here — no gate.
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_back'),
      };
    }

    if (step === 'pick') {
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_back'),
        primaryButtonDisabled: !this.pickValid(),
      };
    }

    if (step === 'details') {
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_back'),
        primaryButtonDisabled: !this.detailsValid(),
      };
    }

    if (step === 'config') {
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_back'),
        primaryButtonDisabled: !this.formValid(),
      };
    }

    if (step === 'tasks') {
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_back'),
        // Tasks are optional; advancing is always allowed.
        primaryButtonDisabled: false,
      };
    }

    // review step
    const loading = this._isLoading();
    return {
      ...base,
      primaryButtonText: loading ? t('primary_creating') : t('primary_create'),
      secondaryButtonText: t('secondary_back'),
      primaryButtonDisabled: loading,
      primaryButtonLoading: loading,
    };
  });

  constructor() {
    effect(() => {
      if (!this.isVisible()) {
        this.resetModalState();
      }
    });

    effect(() => {
      if (!this.isVisible()) return;
      // Refresh today's date so the date input + min reflect the current day,
      // not the day the component was first constructed.
      const today = this.getTodayDate();
      this.todayDate.set(today);
      this.form.controls.first_due_date.setValue(today);
      // Start on the Type step by default. The one-shot ``initialType``
      // latch below fires next and skips ahead to Details if a caller
      // preseeded the type.
      this._currentStep.set('type');
      this._maxReachedStep.set('type');
      this.templateLanguage.set(this.stateStore.orgDefaultContentLanguage());
      this.loadDocumentsForCurrentOrg();
    });

    // One-shot prefill patch — applied on the first visibility flip and
    // then latched via `_hasPatchedFromPrefill` so subsequent parent
    // re-renders don't clobber user edits within the same open session.
    // Read the inputs at effect scope so late-arriving prefills still
    // patch on the next tick; the write happens inside `untracked` so we
    // don't create write-during-read cycles.
    effect(() => {
      if (!this.isVisible() || this._hasPatchedFromPrefill()) return;
      const name = this.pendingName();
      const description = this.pendingDescription();
      const status = this.pendingImplementationStatus();
      const typeSeed = this.initialType();
      if (name == null && description == null && status == null && typeSeed == null) return;
      untracked(() => {
        if (name != null) this.form.controls.name.setValue(name);
        if (description != null) this.form.controls.description.setValue(description);
        this._prefillImplementationStatus.set(status);
        if (typeSeed != null) {
          // Caller already picked the type (DPIA measure picker's card
          // grid, etc.) — skip the Type step. Land on the first
          // downstream step of that type's flow: ``'pick'`` for
          // Template / Composite (their dedicated picker screen),
          // ``'details'`` for Custom (no picker). User can still
          // back-nav to Type and change their mind.
          this._selectedType.set(typeSeed);
          this.setStep(typeSeed === 'custom' ? 'details' : 'pick');
        }
        this._hasPatchedFromPrefill.set(true);
      });
    });

    // Auto-load the control-templates catalogue the moment the user
    // picks Template or Composite. Previously loading was gated on
    // an explicit "Link a template" toggle click; the picker is now
    // always visible while in those types so we warm the cache as
    // soon as the row-state choice commits. Idempotent — repeat
    // toggles don't refetch (see ``ensureControlTemplatesLoaded``).
    effect(() => {
      const type = this._selectedType();
      if (type === 'template' || type === 'composite') {
        untracked(() => this.ensureControlTemplatesLoaded());
      }
    });

    // Existing-controls candidate fetch (VER-284). Fires whenever the
    // Template OR Composite selection changes; debounced 300ms so rapid
    // toggle flicks collapse into one round-trip; switchMap cancels
    // in-flight prior requests when a new selection arrives. Composite
    // feeds the reuse panel below the multi-picker; Template feeds an
    // advisory hint below the picked-template strip.
    //
    // Error handling: HTTP failures set ``reuseCandidatesError`` so
    // the surface can render "couldn't check for duplicates" instead
    // of a false "no duplicates" empty. Empty template ids resolve
    // immediately without a network call.
    const templateIdsForFetch = computed(() => {
      const type = this._selectedType();
      if (type === 'composite') {
        return this.selectedControlTemplates()
          .map(t => t.id)
          .filter(Boolean);
      }
      if (type === 'template') {
        const t = this.selectedControlTemplate();
        return t ? [t.id] : [];
      }
      return [];
    });

    const shallowIdsEqual = (a: string[], b: string[]) =>
      a.length === b.length && a.every((v, i) => v === b[i]);

    merge(
      toObservable(templateIdsForFetch).pipe(
        distinctUntilChanged(shallowIdsEqual),
        // A fresh selection re-arms the advisory hint (per-decision
        // dismissal, not per-modal-session). Placed AFTER dedup so
        // no-op emissions from unrelated signal churn don't undo the
        // user's dismiss.
        tap(() => this.templateReuseHintDismissed.set(false))
      ),
      // Retry path: re-emits the current selection. Bypasses
      // distinctUntilChanged so a click after a failure re-fires
      // even when the ids haven't changed.
      this._retryReuseFetch$.pipe(map(() => templateIdsForFetch()))
    )
      .pipe(
        debounceTime(300),
        switchMap(ids => {
          if (ids.length === 0) {
            return of({
              candidates: [] as readonly ExistingControlMatch[],
              error: null as string | null,
            });
          }
          const orgId = this.selectedOrg()?.principal_id;
          if (!orgId) {
            return of({
              candidates: [] as readonly ExistingControlMatch[],
              error: null as string | null,
            });
          }
          this.reuseCandidatesLoading.set(true);
          this.reuseCandidatesError.set(null);
          return this.api.getExistingControlsForTemplates(orgId, ids).pipe(
            map(candidates => ({
              candidates: candidates as readonly ExistingControlMatch[],
              error: null as string | null,
            })),
            catchError(() =>
              of({
                candidates: [] as readonly ExistingControlMatch[],
                error: 'load_failed' as string | null,
              })
            )
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ candidates, error }) => {
        this.reuseCandidates.set(candidates);
        this.reuseCandidatesLoading.set(false);
        this.reuseCandidatesError.set(error);
        // Prune stale composite reuse ids the user picked earlier but
        // that no longer appear in the fresh candidate set. Only
        // relevant on success — on error we keep prior picks (the BE
        // will 422 stale ids, but the network hiccup shouldn't wipe
        // the user's decision).
        if (error === null) {
          const validIds = new Set(candidates.map(c => c.control_id));
          const pruned = this.compositeReuseIds().filter(id => validIds.has(id));
          if (pruned.length !== this.compositeReuseIds().length) {
            this.compositeReuseIds.set(pruned);
          }
        }
      });
  }

  /** Advisory-hint dismiss handler. Sets the per-pick dismiss flag;
   *  changing the picked template auto-clears the flag so a fresh
   *  pick re-surfaces the hint. */
  dismissTemplateReuseHint(): void {
    this.templateReuseHintDismissed.set(true);
  }

  /** Manual retry for the reuse-candidate fetch. Called from error-
   *  state UI in the reuse panel + Template advisory. */
  retryReuseFetch(): void {
    this._retryReuseFetch$.next();
  }

  /** Today in YYYY-MM-DD (local time) — the format consumed by `<input type="date">`. */
  private getTodayDate(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /** Block any key that isn't a digit or a benign navigation/editing key. */
  onIntervalKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey) return;
    const allowed = [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];
    if (allowed.includes(event.key)) return;
    if (/^\d$/.test(event.key)) return;
    event.preventDefault();
  }

  /** Cancel a paste if the clipboard text isn't a positive integer. */
  onIntervalPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text')?.trim() ?? '';
    if (!/^\d+$/.test(pasted) || parseInt(pasted, 10) < 1) {
      event.preventDefault();
    }
  }

  onContinue(): void {
    const step = this._currentStep();
    if (step === 'review') {
      this.submitControl();
      return;
    }
    // Per-step gates. The primary-button-disabled logic in
    // ``modalConfig`` already prevents most of these branches from
    // firing, but we defend inline too — a keyboard-triggered
    // continue that raced ``modalConfig`` shouldn't slip through.
    if (step === 'type' && this._selectedType() == null) return;
    if (step === 'pick' && !this.pickValid()) return;
    if (step === 'details' && !this.detailsValid()) return;
    // ``config`` / ``tasks`` have their own field-level gates via
    // ``formValid`` / task-form validation surfaced through
    // ``modalConfig`` — nothing to check inline here.
    const list = this.steps();
    const i = list.indexOf(step);
    if (i < 0 || i + 1 >= list.length) return;
    this.clearError();
    this.setStep(list[i + 1]);
  }

  onDismiss(): void {
    if (this.isTerminalState()) return;
    this.cancelled.emit();
  }

  onBack(): void {
    if (this.isTerminalState()) return;
    this.clearError();
    const step = this._currentStep();
    const list = this.steps();
    const i = list.indexOf(step);
    if (i <= 0) {
      // Already on the first step — Back == cancel. Matches the
      // pre-refactor behaviour exactly (type is always index 0).
      this.cancelled.emit();
      return;
    }
    this._currentStep.set(list[i - 1]);
  }

  readonly hasUnsavedWork = computed(() => {
    this.formValue(); // track form changes so dirty state is reactive
    if (this._taskTemplates().length > 0) return true;
    if (this.selectedAssignees().length > 0) return true;
    // Any template selection or advance past the initial Type step
    // counts as unsaved work. Type is always the first step so any
    // non-type currentStep implies the user has moved forward.
    if (this.selectedControlTemplate() || this.selectedControlTemplates().length > 0) return true;
    return this.form.dirty || this._currentStep() !== 'type';
  });

  private submitControl(): void {
    if (this.form.invalid) return;

    const orgId = this.selectedOrg()?.principal_id ?? this.stateStore.selectedOrgId();
    if (!orgId) return;

    const raw = this.form.getRawValue();

    // Inline task templates → ControlTaskSpec[]. The backend implicitly links
    // each task to the control being created, so link_kind / link_entity_id
    // selections from the inline editor are intentionally dropped here.
    const tasks: ControlTaskSpec[] = this._taskTemplates().map(t => {
      const spec: ControlTaskSpec = {
        name: t.title,
        task_type: t.task_type,
        needs_approval: t.needs_approval,
        recurring: t.recurring,
        due_date: t.due_date || null,
      };
      if (t.description?.trim()) spec.description = t.description.trim();
      if (t.task_type === TaskType.CREATE && t.link_kind && t.link_kind !== 'this_control') {
        spec.create_entity_category = t.link_kind as CreateEntityCategory;
        if (t.create_entity_subtype) spec.create_entity_subtype = t.create_entity_subtype;
      }
      return spec;
    });

    // Preset recurrences (Monthly/Quarterly/Semi-annually/Annually) send
    // MONTH + N months so the backend cron lands the next cycle on the
    // matching calendar day. Custom sends DAY + the user-entered day count.
    const recurrence = this.effectiveRecurrence();

    const prefillStatus = this._prefillImplementationStatus();
    // Lineage — Linked (template_id) OR Composite (component list) OR
    // Custom (both null). Mode toggle already ensures only one signal
    // holds a value at save-time (see ``setTemplatePickerMode``); this
    // block collapses the two into the request shape without leaking
    // the other mode's field onto the wire.
    const compositeSelection = this.selectedControlTemplates();
    const isComposite = this.templatePickerMode() === 'composite' && compositeSelection.length > 0;
    const composite_template_components = isComposite
      ? compositeSelection.map(t => ({
          template_id: t.id,
          // FE-side snapshot for logging / audit inspection. BE
          // canonicalises against ``templates.control_templates`` on
          // create so a stale value here is harmless — a bad
          // ``template_id`` would surface as 404 before the snapshot
          // is read.
          template_reference: t.control_reference ?? '',
        }))
      : undefined;
    // Composite reuse (VER-284) — only meaningful in composite mode.
    // The <app-composite-reuse-panel> populates these; when absent,
    // the composite create falls back to "create everything from
    // scratch" semantics. The BE model validator rejects reuse fields
    // when composite_template_components is null so we defensively
    // scope this branch to isComposite.
    const reuseControlIds = isComposite ? this.compositeReuseIds() : undefined;
    const createGapFiller = isComposite ? this.compositeCreateGapFiller() : undefined;

    const request: ControlCreateRequest = {
      org_id: orgId,
      name: raw.name!,
      // Category is required by the backend; the form gates Next on this.
      category: raw.category!,
      description: raw.description || undefined,
      framework_ids: raw.framework_ids?.length ? raw.framework_ids : undefined,
      document_ids: raw.document_ids?.length ? raw.document_ids : undefined,
      recurrence_unit: recurrence?.unit,
      recurrence_interval: recurrence?.interval,
      start_date: raw.first_due_date || undefined,
      tasks: tasks.length ? tasks : undefined,
      assignees: this.selectedAssignees().length ? this.selectedAssignees() : undefined,
      template_id: isComposite ? undefined : this.selectedControlTemplate()?.id,
      composite_template_components,
      reuse_control_ids:
        reuseControlIds && reuseControlIds.length > 0 ? [...reuseControlIds] : undefined,
      create_gap_filler: createGapFiller,
      language: this.templateLanguage(),
      // Seed the control's status from the upstream flow's context (e.g.
      // a DPIA measure with coverage=FULL mints an IN_PLACE control). When
      // no prefill is set the field is omitted → BE defaults to GAP.
      implementation_status: prefillStatus ?? undefined,
    };

    this._isLoading.set(true);
    this.clearError();

    this.api
      .createControl(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: control => {
          this._isLoading.set(false);
          this._successMessage.set(
            this.transloco.translate('modals.create_control.success_created')
          );
          this._successTimer = setTimeout(() => this.controlCreated.emit(control), 1500);
        },
        error: err => {
          this._isLoading.set(false);
          // Name collision is a 409 the user can fix by editing the field —
          // our own sentence, and never a denial.
          const raw = err?.error?.message || err?.message || '';
          if (/already exists in this org/i.test(raw)) {
            this.setError(
              this.transloco.translate('modals.create_control.error_duplicate_control')
            );
            return;
          }
          this.setErrorFrom(
            err,
            this.transloco.translate('modals.create_control.error_create_failed')
          );
        },
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Tasks step — handlers for the inline template editor.
  // ─────────────────────────────────────────────────────────────────────────

  openAddTask(): void {
    this._editingTaskIndex.set(null);
    this.entitySearchQuery.set('');
    const isRecurrent = this.isControlRecurrent();
    this.taskForm.reset({
      title: '',
      task_type: null,
      link_kind: null,
      link_entity_id: null,
      create_entity_subtype: null,
      description: '',
      needs_approval: false,
      recurring: isRecurrent,
      due_date: null,
    });
    this.taskForm.controls.needs_approval.enable();
    if (isRecurrent) {
      this.taskForm.controls.recurring.enable();
    } else {
      this.taskForm.controls.recurring.disable();
    }
    this._taskFormOpen.set(true);
  }

  openEditTask(index: number): void {
    const template = this._taskTemplates()[index];
    if (!template) return;
    this._editingTaskIndex.set(index);
    this.entitySearchQuery.set('');
    this.taskForm.reset({
      title: template.title,
      task_type: template.task_type,
      link_kind: template.link_kind === 'this_control' ? null : template.link_kind,
      link_entity_id: template.link_entity_id,
      create_entity_subtype: template.create_entity_subtype ?? null,
      description: template.description,
      needs_approval: template.needs_approval,
      recurring: template.recurring,
      due_date: template.due_date ?? null,
    });
    if (template.task_type === TaskType.CREATE) {
      this.taskForm.controls.needs_approval.disable();
      this.taskForm.controls.recurring.disable();
    } else {
      this.taskForm.controls.needs_approval.enable();
      if (this.isControlRecurrent()) {
        this.taskForm.controls.recurring.enable();
      } else {
        this.taskForm.controls.recurring.disable();
      }
    }
    this._taskFormOpen.set(true);
    if (template.link_kind && template.link_kind !== 'this_control') {
      this.loadEntitiesForKind(template.link_kind);
    }
  }

  deleteTask(index: number): void {
    const list = [...this._taskTemplates()];
    list.splice(index, 1);
    this._taskTemplates.set(list);
  }

  saveTask(): void {
    if (!this.taskFormSaveEnabled()) return;
    const raw = this.taskForm.getRawValue();
    const type = raw.task_type as TaskType;

    let linkKind: TaskLinkKind | null = raw.link_kind ?? null;
    let linkEntityId: string | null = raw.link_entity_id ?? null;
    if (type === TaskType.UPLOAD) {
      linkKind = 'this_control';
      linkEntityId = null;
    } else if (type === TaskType.CREATE) {
      linkEntityId = null;
    }

    const template: TaskTemplate = {
      title: (raw.title ?? '').trim(),
      task_type: type,
      link_kind: linkKind,
      link_entity_id: linkEntityId,
      description: raw.description ?? '',
      needs_approval: !!raw.needs_approval,
      recurring: !!raw.recurring,
      due_date: raw.due_date || null,
      create_entity_subtype: type === TaskType.CREATE ? (raw.create_entity_subtype ?? null) : null,
    };

    const list = [...this._taskTemplates()];
    const editing = this._editingTaskIndex();
    if (editing !== null) {
      list[editing] = template;
    } else {
      list.push(template);
    }
    this._taskTemplates.set(list);
    this._taskFormOpen.set(false);
    this._editingTaskIndex.set(null);
  }

  cancelTaskForm(): void {
    this._taskFormOpen.set(false);
    this._editingTaskIndex.set(null);
  }

  selectTaskType(type: TaskType): void {
    this.taskForm.controls.task_type.setValue(type);
    this.taskForm.controls.create_entity_subtype.setValue(null);
    if (type === TaskType.UPLOAD) {
      this.taskForm.controls.link_kind.setValue(null);
      this.taskForm.controls.link_entity_id.setValue(null);
    }
    if (type === TaskType.CREATE) {
      this.taskForm.controls.link_entity_id.setValue(null);
      this.taskForm.controls.needs_approval.setValue(true);
      this.taskForm.controls.needs_approval.disable();
      this.taskForm.controls.recurring.setValue(false);
      this.taskForm.controls.recurring.disable();
    } else {
      this.taskForm.controls.needs_approval.enable();
      if (this.isControlRecurrent()) {
        this.taskForm.controls.recurring.enable();
      } else {
        this.taskForm.controls.recurring.disable();
      }
    }
  }

  selectTaskLink(kind: TaskLinkKind): void {
    if (this.taskForm.controls.link_kind.value !== kind) {
      this.taskForm.controls.link_entity_id.setValue(null);
      this.taskForm.controls.create_entity_subtype.setValue(null);
      this.entitySearchQuery.set('');
    }
    this.taskForm.controls.link_kind.setValue(kind);
    if (this.isCreateTask()) {
      if (kind === 'assessment') {
        this.taskForm.controls.create_entity_subtype.setValue(
          AssessmentTypeEnum.AI_RISK_ASSESSMENT
        );
      }
    } else {
      this.loadEntitiesForKind(kind);
    }
  }

  selectTaskEntity(id: string): void {
    const current = this.taskForm.controls.link_entity_id.value;
    this.taskForm.controls.link_entity_id.setValue(current === id ? null : id);
  }

  selectEntitySubtype(subtype: string): void {
    const current = this.taskForm.controls.create_entity_subtype.value;
    this.taskForm.controls.create_entity_subtype.setValue(current === subtype ? null : subtype);
  }

  onEntitySearchValue(value: string): void {
    this.entitySearchQuery.set(value);
  }

  /**
   * Lazy-load the data source for the picked entity kind. Document & Framework
   * data is already hydrated for the Configuration step; the rest is fetched
   * on first use and cached for the lifetime of this modal instance.
   */
  private loadEntitiesForKind(kind: TaskLinkKind | null): void {
    if (!kind || kind === 'this_control') return;
    const orgId = this.selectedOrg()?.principal_id ?? this.stateStore.selectedOrgId();
    if (!orgId) return;

    if (kind === 'control' && this._controls().length === 0 && !this._controlsLoading()) {
      this._controlsLoading.set(true);
      this.api
        .getControls(orgId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: controls => {
            this._controls.set(controls);
            this._controlsLoading.set(false);
          },
          error: () => this._controlsLoading.set(false),
        });
    }

    if (kind === 'vendor' && !this.stateStore.vendorsLoading()) {
      this.stateStore.loadVendors();
    }

    if (kind === 'assessment' && this._assessments().length === 0 && !this._assessmentsLoading()) {
      this._assessmentsLoading.set(true);
      this.api
        .getAssessments(orgId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => {
            this._assessments.set(res.assessments);
            this._assessmentsLoading.set(false);
          },
          error: () => this._assessmentsLoading.set(false),
        });
    }
  }

  taskTypeLabel(type: TaskType | null): string {
    if (!type) return '';
    return (
      this.taskTypeOptions().find(o => o.value === type)?.label ??
      this.transloco.translate('modals.create_control.task_type_other')
    );
  }

  taskTypeIcon(type: TaskType): string {
    switch (type) {
      case TaskType.UPLOAD:
        return 'upload';
      case TaskType.REVIEW:
        return 'fact_check';
      case TaskType.CREATE:
        return 'add_circle';
      case TaskType.ASSESS:
        return 'assessment';
      default:
        return 'task_alt';
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Template linking — handlers
  // ─────────────────────────────────────────────────────────────────────────

  /** Fetch the control-template catalogue once per modal open. Called
   *  from the ``selectedType()`` effect when the user picks Template
   *  or Composite — since the picker is now ALWAYS visible while in
   *  those types, we can't wait for a toggle click to warm the cache.
   *  Idempotent: if the list is already loaded / loading, no-op. */
  private ensureControlTemplatesLoaded(): void {
    if (this._controlTemplates().length > 0 || this._controlTemplatesLoading()) return;
    this._controlTemplatesLoading.set(true);
    this.api
      .getControlTemplates({})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: templates => {
          this._controlTemplates.set(templates);
          this._controlTemplatesLoading.set(false);
        },
        error: () => this._controlTemplatesLoading.set(false),
      });
  }

  selectControlTemplate(template: ControlTemplate): void {
    this.selectedControlTemplate.set(template);
    this.templateSearchQuery.set('');
    this.applyTemplateToForm(template);
  }

  /** Composite mode: toggle a template's membership in the selection.
   *  Adding a template re-runs ``applyCompositeSelectionToForm`` to fuse
   *  a name from every current member's ``control_reference``; removing
   *  the last member does NOT auto-clear the form so the user's own
   *  edits (description, task templates) survive the last uncheck. */
  toggleCompositeTemplate(template: ControlTemplate): void {
    const current = this.selectedControlTemplates();
    const already = current.some(t => t.id === template.id);
    const next = already ? current.filter(t => t.id !== template.id) : [...current, template];
    this.selectedControlTemplates.set(next);
    this.applyCompositeSelectionToForm(next);
  }

  /** Type-step click handler. Sets the row-state choice, clears any
   *  stale template picker selections from a previous choice, and
   *  resets template-prefilled form fields when necessary — the same
   *  drift-avoidance the old ``setTemplatePickerMode`` fixed, now
   *  bound to the type-step semantic rather than an in-Details toggle.
   *
   *  Called from the type-step's card grid AND from the ``initialType``
   *  one-shot latch (which lets external callers seed the choice
   *  before the modal renders). The type step itself doesn't advance
   *  the wizard — that happens through the shared ``onContinue``
   *  Next-button click after the user has committed the pick. */
  selectType(type: ControlCreationType): void {
    if (this._selectedType() === type) return;
    const outgoingHadSelection =
      !!this.selectedControlTemplate() || this.selectedControlTemplates().length > 0;
    this._selectedType.set(type);
    // Switching type reshapes `steps()` (custom drops the `pick` step)
    // and drops the row-state signals below. Any prior high-water mark
    // past `type` referenced steps that used the OLD type's data, so
    // roll it back to `type` and let the user re-walk the (possibly
    // reshaped) flow.
    this._maxReachedStep.set('type');
    // Drop EVERY signal that belongs to a specific row-state so the
    // outgoing choice can't smuggle stale data into the incoming
    // choice's request. The BE model validator rejects reuse fields
    // without a composite context, but stopping the drift at the FE
    // is friendlier + prevents "why is compositeReuseIds set when
    // I picked Custom?" bug reports.
    this.selectedControlTemplate.set(null);
    this.selectedControlTemplates.set([]);
    this.compositeReuseIds.set([]);
    this.compositeCreateGapFiller.set(true);
    if (outgoingHadSelection) {
      // Reset only the fields the two ``applyTemplateToForm`` helpers
      // touch — matches ``clearControlTemplate``'s reset shape.
      this.form.patchValue({ name: '', category: null, description: '' });
      this._taskTemplates.set([]);
    }
    this.templateSearchQuery.set('');
  }

  clearControlTemplate(): void {
    this.selectedControlTemplate.set(null);
    this.selectedControlTemplates.set([]);
    this.form.patchValue({
      name: '',
      category: null,
      description: '',
    });
    this._taskTemplates.set([]);
  }

  onLanguageChange(lang: EntityLanguage): void {
    this.templateLanguage.set(lang);
    const tpl = this.selectedControlTemplate();
    if (tpl) this.applyTemplateToForm(tpl);
    if (this.templatePickerMode() === 'composite') {
      this.applyCompositeSelectionToForm(this.selectedControlTemplates());
    }
  }

  onTemplateSearchValue(value: string): void {
    this.templateSearchQuery.set(value);
  }

  private applyTemplateToForm(template: ControlTemplate): void {
    this.form.patchValue({
      name: this.localizedField(template.name, template.name_da),
      category: template.category ?? null,
      description: this.localizedField(template.rationale, template.rationale_da),
    });

    // Map template recurrence to form preset
    const preset = apiFieldsToRecurrencePreset(
      template.recurrence_unit,
      template.recurrence_interval
    );
    if (preset) {
      this.form.controls.recurrence.setValue(preset);
      if (preset === ControlRecurrence.CUSTOM && template.recurrence_interval) {
        this.form.controls.custom_recurrence_days.setValue(template.recurrence_interval);
      }
    }

    // Map template tasks
    if (template.tasks?.length) {
      this._taskTemplates.set(
        template.tasks.map(t => ({
          title: this.localizedField(t.name, t.name_da),
          task_type: t.task_type ?? TaskType.OTHER,
          link_kind: null,
          link_entity_id: null,
          description: this.localizedField(t.description, t.description_da),
          needs_approval: t.needs_approval,
          recurring: t.recurring,
          due_date: null,
          create_entity_subtype: null,
        }))
      );
    }
  }

  /** Composite variant of ``applyTemplateToForm``.
   *
   *  The fused ``name`` is a joined reference list (e.g.
   *  ``"§ 5.24 / 5.25 / 5.26"``) — matches the human-readable shape the
   *  import assistant uses today for AI-detected composites (see
   *  ``PreviewControlNode.control_reference`` when a composite is
   *  present). ``category`` is taken from the first selected component
   *  since composites live under one taxonomic bucket per audit
   *  convention; the user can always override in the form.
   *
   *  Tasks are UNIONED across every selected component's ``tasks[]``,
   *  deduped by lowercased localised title (first-seen wins on tie).
   *  Symmetric with single-mode ``applyTemplateToForm`` which seeds the
   *  Tasks step from the single template's task list — composite mode
   *  MUST NOT silently drop the component templates' tasks (the
   *  original bug this method was fixing was doing exactly that). The
   *  user can add / remove / edit in the Tasks step exactly as today.
   *
   *  Empty selection returns early — user's own edits (description,
   *  ad-hoc task additions) survive a mid-flow uncheck. This asymmetry
   *  with ``clearControlTemplate`` is intentional: uncheck is a
   *  mid-selection action, clear is the explicit "start over" gesture. */
  private applyCompositeSelectionToForm(selection: readonly ControlTemplate[]): void {
    if (selection.length === 0) return;
    const refs = selection.map(t => t.control_reference).filter((r): r is string => !!r);
    const fusedName = refs.length
      ? refs.join(' / ')
      : this.localizedField(selection[0].name, selection[0].name_da);
    this.form.patchValue({
      name: fusedName,
      category: selection[0].category ?? null,
    });

    // Union of component tasks, dedup by lowercased localised title.
    // Preserves insertion order (first-seen template's task wins the
    // tie key). Matches single-mode's replace-tasks-on-selection
    // shape: the Tasks step is a fresh view of the composite's
    // combined task-templates every time the selection changes.
    const seenTitles = new Set<string>();
    const unionedTasks: TaskTemplate[] = [];
    for (const template of selection) {
      for (const t of template.tasks ?? []) {
        const localisedTitle = this.localizedField(t.name, t.name_da);
        const key = localisedTitle.trim().toLowerCase();
        if (!key || seenTitles.has(key)) continue;
        seenTitles.add(key);
        unionedTasks.push({
          title: localisedTitle,
          task_type: t.task_type ?? TaskType.OTHER,
          link_kind: null,
          link_entity_id: null,
          description: this.localizedField(t.description, t.description_da),
          needs_approval: t.needs_approval,
          recurring: t.recurring,
          due_date: null,
          create_entity_subtype: null,
        });
      }
    }
    this._taskTemplates.set(unionedTasks);
  }

  private loadDocumentsForOrg(orgId: string): void {
    this.loadingDocuments.set(true);
    this.orgDocuments.set([]);

    this.api
      .getAllDocuments(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: documents => {
          this.orgDocuments.set(documents);
          this.loadingDocuments.set(false);
        },
        error: () => {
          this.loadingDocuments.set(false);
        },
      });
  }

  private loadDocumentsForCurrentOrg(): void {
    const orgId = this.selectedOrg()?.principal_id ?? this.stateStore.selectedOrgId();
    if (!orgId) return;
    this.loadControlCategoriesForOrg(orgId);
    this.loadDocumentsForOrg(orgId);
    this.loadFrameworksForOrg(orgId);
  }

  private loadControlCategoriesForOrg(orgId: string): void {
    this.api
      .getControlCategories(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: categories => {
          this.controlCategories.set(categories);
        },
        error: () => {
          this.controlCategories.set([]);
        },
      });
  }

  private loadFrameworksForOrg(orgId: string): void {
    this.loadingFrameworks.set(true);
    this.orgFrameworks.set([]);

    this.api
      .getFrameworks(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: frameworks => {
          this.orgFrameworks.set(frameworks);
          this.loadingFrameworks.set(false);
        },
        error: () => {
          this.loadingFrameworks.set(false);
        },
      });
  }

  protected onAssigneesChange(ids: string[]): void {
    this.selectedAssignees.set(ids);
  }

  private resetModalState(): void {
    if (this._successTimer) {
      clearTimeout(this._successTimer);
      this._successTimer = null;
    }
    const today = this.getTodayDate();
    this.todayDate.set(today);
    this.form.reset({
      name: '',
      category: null,
      description: '',
      framework_ids: [],
      document_ids: [],
      recurrence: ControlRecurrence.MONTHLY,
      custom_recurrence_days: null,
      first_due_date: today,
    });
    this.clearError();
    this._isLoading.set(false);
    this._successMessage.set(null);
    this.orgDocuments.set([]);
    this.orgFrameworks.set([]);
    this.controlCategories.set([]);
    this._taskTemplates.set([]);
    this._taskFormOpen.set(false);
    this._editingTaskIndex.set(null);
    this.selectedAssignees.set([]);
    this._controls.set([]);
    this._assessments.set([]);
    this.entitySearchQuery.set('');
    this.selectedControlTemplate.set(null);
    this.selectedControlTemplates.set([]);
    this._selectedType.set(null);
    this._maxReachedStep.set('type');
    this.compositeReuseIds.set([]);
    this.compositeCreateGapFiller.set(true);
    this.reuseCandidates.set([]);
    this.reuseCandidatesLoading.set(false);
    this.reuseCandidatesError.set(null);
    this.templateReuseHintDismissed.set(false);
    this.templateSearchQuery.set('');
    this.templateLanguage.set(this.stateStore.orgDefaultContentLanguage());
    this._currentStep.set('type');
    // Reset the prefill guard so a subsequent reopen with fresh prefills
    // re-patches (the prefill effect only fires when this flag is false).
    this._hasPatchedFromPrefill.set(false);
    this._prefillImplementationStatus.set(null);
  }
}
